import {
  FC,
  forwardRef,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Button, Flex, InputRef } from 'antd';
import { Popover } from '../../components/Popover';
import { TextEllipsis } from '../../components/TextEllipsis';
import { Tooltip } from '../../components/Tooltip';
import { flexRender, Header, SortDirection } from '@tanstack/react-table';
import * as Icon from 'react-feather';
import {
  colorBgLayout,
  colorGray,
  colorLink,
  components,
  fontWeightStrong,
} from '@atlas-frontend/design-tokens';
import { cleanArray } from '../../utils';
import { ColumnResizeHandle } from '../columnSizing/ColumnResizeHandle';
import { SortIndicator } from './SortIndicator';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { stickyStyle } from './stickyStyle';
import { useFilterInput } from '../columnFilters/filterInputs';
import translations from './HeaderCellTemplate.translations.json';
import { WithErrorRowsCheckbox } from '../../form/table/tanstack/WithErrorRowsCheckbox';
import { useFormTableErrors } from '../../form';
import {
  getHeaderActionsWidthPx,
  getHeaderMinWidthPx,
} from '../columnSizing/getHeaderWidthStyle';
import { useSearchParams } from 'react-router-dom';

const { addTranslations, useTranslation } = i18nNsFactory('table-header-cell');
addTranslations(translations);

export interface HeaderCellTemplateProps extends HTMLAttributes<HTMLTableCellElement> {
  header: Header<any, unknown>;
  appearsActive?: boolean;
  appearsDisabled?: boolean;
  columnResizeDisabled?: boolean;
  disabled?: boolean;
  as?: 'div';
}

export const HeaderCellTemplate = forwardRef<
  HTMLTableCellElement,
  HeaderCellTemplateProps
>((props, ref) => {
  const {
    appearsActive,
    appearsDisabled,
    columnResizeDisabled,
    header,
    disabled = false,
    as,
    ...rest
  } = props;

  const { t } = useTranslation();

  const errorInfo = useFormTableErrors();
  const prettyDisplayErrors = !!errorInfo;

  const canSort = useMemo(() => header.column.getCanSort(), [header.column]);
  const canFilter = useMemo(
    () => header.column.getCanFilter(),
    [header.column],
  );

  const { filterFn } = header.column.columnDef;
  const FilterInput = useFilterInput(filterFn);

  const content = flexRender(
    header.column.columnDef.header,
    header.getContext(),
  );
  const renderedHeaderContent = shouldAutoEllipsisHeader(content) ? (
    <TextEllipsis style={{ display: 'block', width: '100%' }} placement="top">
      {content}
    </TextEllipsis>
  ) : (
    content
  );

  const className = useMemo(
    () =>
      cleanArray([
        (header.column.columnDef as any).accessorKey,
        header.column.columnDef.meta?.className,
        header.column.columnDef.meta?.headerClassName,
      ])?.join(' '),
    [header],
  );

  const sortOrder: SortDirection | false = header.column.getIsSorted();

  const filterValue = header.column.getFilterValue();
  const isFilterActive = Array.isArray(filterValue)
    ? filterValue.some(isFilterValueActive)
    : isFilterValueActive(filterValue);

  // Check if error filter is active for this column
  const [searchParams] = useSearchParams();
  const errorFilterColumns = useMemo(() => {
    return searchParams.get('errorFilterColumns')?.split(',') || [];
  }, [searchParams]);
  const isErrorFilterActive =
    errorFilterColumns.some((column) => column === header.column.id) || false;
  const showFilterAsActive = isFilterActive || isErrorFilterActive;
  const onSort = disabled ? undefined : header.column.getToggleSortingHandler();
  const actionsWidthPx = useMemo(
    () => getHeaderActionsWidthPx({ canSort, canFilter }),
    [canFilter, canSort],
  );
  const headerMinWidth = useMemo(() => {
    const cellPaddingWidth = components.Table.cellPaddingInlineMD * 2;

    return `${getHeaderMinWidthPx({
      canSort,
      canFilter,
      cellPaddingPx: cellPaddingWidth,
    })}px`;
  }, [canFilter, canSort]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterInputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (isFilterOpen) {
      setTimeout(() => {
        filterInputRef.current?.focus();
      });
    }
  }, [isFilterOpen]);

  return (
    <StyledHeaderCell
      as={as as any}
      scope="col"
      aria-sort={
        sortOrder === 'asc'
          ? 'ascending'
          : sortOrder === 'desc'
            ? 'descending'
            : 'none'
      }
      id={`column-header-${header.column.id}`}
      $dark={!!sortOrder}
      $hover={canSort && !appearsActive && !disabled}
      $appearsActive={appearsActive}
      $appearsDisabled={disabled || appearsDisabled}
      $minWidth={headerMinWidth}
      className={className}
      ref={ref}
      {...rest}
    >
      <CellInnerWrapper
        $appearsDisabled={disabled || appearsDisabled}
        className="drag-sortable"
      >
        <Flex align="center" gap="3px">
          <ContentWrapper
            $actionsWidthPx={actionsWidthPx}
            style={appearsActive ? { color: colorLink } : undefined}
            onClick={canSort ? onSort : undefined}
          >
            {renderedHeaderContent}
          </ContentWrapper>
          {(canSort || canFilter) && (
            <ActionsContainer
              $actionsWidthPx={actionsWidthPx}
              align="center"
              gap={3}
              className="exclude-drag-sortable"
            >
              {canSort &&
                (disabled ? (
                  <SortIndicator sort={sortOrder} />
                ) : (
                  <Tooltip
                    title={t(
                      `sorting.${
                        header.column.getNextSortingOrder() || 'reset'
                      }`,
                    )}
                    placement="top"
                    wrapperType="div"
                  >
                    <SortIndicatorButton onClick={onSort}>
                      <SortIndicator sort={sortOrder} />
                    </SortIndicatorButton>
                  </Tooltip>
                ))}

              {canFilter &&
                (disabled ? (
                  <FilterBtn
                    id={header.column.id}
                    isActive={showFilterAsActive}
                  />
                ) : (
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <Popover
                      placement="bottom-end"
                      isOpen={isFilterOpen}
                      onOpenChange={setIsFilterOpen}
                      style={
                        filterFn === 'includesOption'
                          ? { maxHeight: 'none', overflow: 'visible' }
                          : undefined
                      }
                      content={
                        prettyDisplayErrors ? (
                          <WithErrorRowsCheckbox columnId={header.column.id}>
                            <FilterInput
                              ref={filterInputRef}
                              header={header}
                              onBlur={() => setIsFilterOpen(false)}
                            />
                          </WithErrorRowsCheckbox>
                        ) : (
                          <FilterInput
                            ref={filterInputRef}
                            header={header}
                            onBlur={() => setIsFilterOpen(false)}
                          />
                        )
                      }
                    >
                      <Tooltip title={t('filter')} placement="top">
                        <FilterBtn
                          id={header.column.id}
                          isActive={showFilterAsActive}
                          onClick={() => setIsFilterOpen((open) => !open)}
                        />
                      </Tooltip>
                    </Popover>
                  </div>
                ))}
            </ActionsContainer>
          )}
        </Flex>
      </CellInnerWrapper>
      {disabled || columnResizeDisabled ? undefined : (
        <ColumnResizeHandle header={header} />
      )}
    </StyledHeaderCell>
  );
});

const isFilterValueActive = (v: unknown) =>
  typeof v !== 'undefined' && v !== null;

const shouldAutoEllipsisHeader = (
  content: ReactNode,
): content is string | number =>
  typeof content === 'string' || typeof content === 'number';

interface FilterBtnProps {
  id: string;
  isActive: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

const FilterBtn: FC<FilterBtnProps> = ({ id, isActive, onClick }) => (
  <FilterButton
    id={`filter-column-${id}`}
    size="small"
    icon={
      <Icon.Filter size={16} style={{ fill: 'currentColor', strokeWidth: 0 }} />
    }
    type={isActive ? 'link' : 'text'}
    onClick={onClick}
  />
);

const CellInnerWrapper = styled.div<{ $appearsDisabled?: boolean }>`
  padding-block: ${components.Table.cellPaddingBlockMD}px;
  padding-inline: ${components.Table.cellPaddingInlineMD}px;
  overflow: hidden;
  ${({ $appearsDisabled }) => $appearsDisabled && 'opacity: 0.3;'}
`;

const ContentWrapper = styled.div<{ $actionsWidthPx: number }>`
  flex-grow: 1;
  text-align: left;
  min-height: 20px;
  max-width: ${({ $actionsWidthPx }) =>
    $actionsWidthPx > 0 ? `calc(100% - ${$actionsWidthPx}px)` : '100%'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SortIndicatorButton = styled.div`
  cursor: pointer;
`;

const ActionsContainer = styled(Flex)<{ $actionsWidthPx: number }>`
  flex-shrink: 0;
  min-width: ${({ $actionsWidthPx }) => `${$actionsWidthPx}px`};
  justify-content: flex-end;
`;

const FilterButton = styled(Button)`
  font-size: 12px;
  color: ${({ type }) => (type === 'link' ? colorLink : colorGray[6])};
  width: 18px;

  .ant-btn-icon {
    height: 16px;
  }
`;

type StyledHeaderCellProps = {
  $hover?: boolean;
  $dark?: boolean;
  $appearsActive?: boolean;
  $appearsDisabled?: boolean;
  $minWidth?: string;
};

const StyledHeaderCell = styled.th<StyledHeaderCellProps>`
  position: relative;
  background: ${(props) =>
    props.$appearsActive
      ? `linear-gradient(to bottom, ${colorLink}33, ${colorLink}33), ${components.Table.headerSortActiveBg}`
      : props.$appearsDisabled
        ? '#fafafa'
        : props.$dark
          ? components.Table.headerSortActiveBg
          : components.Table.headerBg};
  font-size: 12px;
  font-weight: ${fontWeightStrong};
  letter-spacing: 0.04em;
  box-sizing: border-box;
  transition: all 0.3s;
  min-width: ${(props) => props.$minWidth || 'auto'};

  ${(props) =>
    props.$hover &&
    `
  cursor: pointer;

  &:hover {
    background-color: ${components.Table.headerSortHoverBg};
    cursor: pointer;
  }`}

  &.sortable {
    cursor: pointer;
  }

  ${stickyStyle}

  &.drag {
    background-color: ${colorBgLayout};
    box-shadow: none;

    &:before {
      content: none;
    }
  }
`;
